import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit, Trash2, Database, Tag, MapPin, Home,
  CheckCircle, XCircle, PlayCircle, Save, Award, Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MasterDataManagement = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('service-types');
  
  // Service Types State
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceTypesLoading, setServiceTypesLoading] = useState(false);
  
  // Service Categories State
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Property Types State
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [propertyTypesLoading, setPropertyTypesLoading] = useState(false);
  
  // States State (US States)
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(false);
  
  // Specializations State
  const [specializations, setSpecializations] = useState([]);
  const [specializationsLoading, setSpecializationsLoading] = useState(false);
  
  // Languages State
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  
  // Connect Categories State
  const [connectCategories, setConnectCategories] = useState([]);
  const [connectCategoriesLoading, setConnectCategoriesLoading] = useState(false);
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    loadData();
  }, [activeSection]);

  const loadData = () => {
    if (activeSection === 'service-types') {
      fetchServiceTypes();
    } else if (activeSection === 'categories') {
      fetchCategories();
    } else if (activeSection === 'property-types') {
      fetchPropertyTypes();
    } else if (activeSection === 'states') {
      fetchStates();
    } else if (activeSection === 'specializations') {
      fetchSpecializations();
    } else if (activeSection === 'languages') {
      fetchLanguages();
    } else if (activeSection === 'connect-categories') {
      fetchConnectCategories();
    }
  };

  // ==================== SERVICE TYPES ====================
  const fetchServiceTypes = async () => {
    setServiceTypesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/master-data/service-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceTypes(response.data.service_types || []);
    } catch (error) {
      console.error('Failed to fetch service types:', error);
      toast.error('Failed to fetch service types');
    } finally {
      setServiceTypesLoading(false);
    }
  };

  const handleServiceTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editMode) {
        await axios.put(
          `${API}/admin/master-data/service-types/${currentItem._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service type updated');
      } else {
        await axios.post(
          `${API}/admin/master-data/service-types`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Service type created');
      }
      setDialogOpen(false);
      fetchServiceTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteServiceType = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/master-data/service-types/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchServiceTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleServiceTypeStatus = async (item) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/master-data/service-types/${item._id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(item.is_active ? 'Deactivated' : 'Activated');
      fetchServiceTypes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ==================== SERVICE CATEGORIES ====================
  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/service-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to fetch categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const initializeCategories = async () => {
    if (!window.confirm('Initialize default 5 service categories?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API}/admin/service-categories/initialize`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize');
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editMode) {
        await axios.put(
          `${API}/admin/service-categories/${currentItem._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category updated');
      } else {
        await axios.post(
          `${API}/admin/service-categories`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deleteCategory = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/service-categories/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleCategoryStatus = async (item) => {
    try {
      const token = localStorage.getItem('adminToken');
      const newStatus = item.status === 'active' ? 'inactive' : 'active';
      await axios.put(
        `${API}/admin/service-categories/${item._id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Category ${newStatus}`);
      fetchCategories();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ==================== STATES ====================
  // ==================== PROPERTY TYPES ====================
  const fetchPropertyTypes = async () => {
    setPropertyTypesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/master-data/property-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPropertyTypes(response.data.property_types || []);
    } catch (error) {
      console.error('Failed to fetch property types:', error);
      toast.error('Failed to fetch property types');
    } finally {
      setPropertyTypesLoading(false);
    }
  };

  const handlePropertyTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editMode) {
        await axios.put(
          `${API}/admin/master-data/property-types/${currentItem._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Property type updated');
      } else {
        await axios.post(
          `${API}/admin/master-data/property-types`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Property type created');
      }
      setDialogOpen(false);
      fetchPropertyTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const deletePropertyType = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/master-data/property-types/${item._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted');
      fetchPropertyTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const togglePropertyTypeStatus = async (item) => {
    try {
      const token = localStorage.getItem('adminToken');
      await axios.put(
        `${API}/admin/master-data/property-types/${item._id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(item.is_active ? 'Deactivated' : 'Activated');
      fetchPropertyTypes();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // ==================== STATES (continued) ====================
  const fetchStates = async () => {
    setStatesLoading(true);
    // For now, use static US states data
    // In future, this can be from backend if you want to make it fully dynamic
    const usStates = [
      { code: 'AL', name: 'Alabama', status: 'active' },
      { code: 'AK', name: 'Alaska', status: 'active' },
      { code: 'AZ', name: 'Arizona', status: 'active' },
      { code: 'AR', name: 'Arkansas', status: 'active' },
      { code: 'CA', name: 'California', status: 'active' },
      { code: 'CO', name: 'Colorado', status: 'active' },
      { code: 'CT', name: 'Connecticut', status: 'active' },
      { code: 'DE', name: 'Delaware', status: 'active' },
      { code: 'FL', name: 'Florida', status: 'active' },
      { code: 'GA', name: 'Georgia', status: 'active' },
      { code: 'HI', name: 'Hawaii', status: 'active' },
      { code: 'ID', name: 'Idaho', status: 'active' },
      { code: 'IL', name: 'Illinois', status: 'active' },
      { code: 'IN', name: 'Indiana', status: 'active' },
      { code: 'IA', name: 'Iowa', status: 'active' },
      { code: 'KS', name: 'Kansas', status: 'active' },
      { code: 'KY', name: 'Kentucky', status: 'active' },
      { code: 'LA', name: 'Louisiana', status: 'active' },
      { code: 'ME', name: 'Maine', status: 'active' },
      { code: 'MD', name: 'Maryland', status: 'active' },
      { code: 'MA', name: 'Massachusetts', status: 'active' },
      { code: 'MI', name: 'Michigan', status: 'active' },
      { code: 'MN', name: 'Minnesota', status: 'active' },
      { code: 'MS', name: 'Mississippi', status: 'active' },
      { code: 'MO', name: 'Missouri', status: 'active' },
      { code: 'MT', name: 'Montana', status: 'active' },
      { code: 'NE', name: 'Nebraska', status: 'active' },
      { code: 'NV', name: 'Nevada', status: 'active' },
      { code: 'NH', name: 'New Hampshire', status: 'active' },
      { code: 'NJ', name: 'New Jersey', status: 'active' },
      { code: 'NM', name: 'New Mexico', status: 'active' },
      { code: 'NY', name: 'New York', status: 'active' },
      { code: 'NC', name: 'North Carolina', status: 'active' },
      { code: 'ND', name: 'North Dakota', status: 'active' },
      { code: 'OH', name: 'Ohio', status: 'active' },
      { code: 'OK', name: 'Oklahoma', status: 'active' },
      { code: 'OR', name: 'Oregon', status: 'active' },
      { code: 'PA', name: 'Pennsylvania', status: 'active' },
      { code: 'RI', name: 'Rhode Island', status: 'active' },
      { code: 'SC', name: 'South Carolina', status: 'active' },
      { code: 'SD', name: 'South Dakota', status: 'active' },
      { code: 'TN', name: 'Tennessee', status: 'active' },
      { code: 'TX', name: 'Texas', status: 'active' },
      { code: 'UT', name: 'Utah', status: 'active' },
      { code: 'VT', name: 'Vermont', status: 'active' },
      { code: 'VA', name: 'Virginia', status: 'active' },
      { code: 'WA', name: 'Washington', status: 'active' },
      { code: 'WV', name: 'West Virginia', status: 'active' },
      { code: 'WI', name: 'Wisconsin', status: 'active' },
      { code: 'WY', name: 'Wyoming', status: 'active' }
    ];
    setStates(usStates);
    setStatesLoading(false);
  };

  // ==================== SPECIALIZATIONS HANDLERS ====================
  const fetchSpecializations = async () => {
    setSpecializationsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/master-data/specializations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setSpecializations(response.data.specializations || []);
    } catch (error) {
      toast.error('Failed to fetch specializations');
    }
    setSpecializationsLoading(false);
  };

  const handleSpecializationSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentItem) {
        await axios.put(`${API}/admin/master-data/specializations/${currentItem._id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        toast.success('Specialization updated');
      } else {
        await axios.post(`${API}/admin/master-data/specializations`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        toast.success('Specialization created');
      }
      setDialogOpen(false);
      fetchSpecializations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save specialization');
    }
  };

  const deleteSpecialization = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/master-data/specializations/${item._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      toast.success('Specialization deleted');
      fetchSpecializations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleSpecializationStatus = async (item) => {
    try {
      await axios.put(`${API}/admin/master-data/specializations/${item._id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      fetchSpecializations();
    } catch (error) { toast.error('Failed to update status'); }
  };

  // ==================== LANGUAGES HANDLERS ====================
  const fetchLanguages = async () => {
    setLanguagesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/master-data/languages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setLanguages(response.data.languages || []);
    } catch (error) {
      toast.error('Failed to fetch languages');
    }
    setLanguagesLoading(false);
  };

  const handleLanguageSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentItem) {
        await axios.put(`${API}/admin/master-data/languages/${currentItem._id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        toast.success('Language updated');
      } else {
        await axios.post(`${API}/admin/master-data/languages`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        toast.success('Language created');
      }
      setDialogOpen(false);
      fetchLanguages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save language');
    }
  };

  const deleteLanguage = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/master-data/languages/${item._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      toast.success('Language deleted');
      fetchLanguages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleLanguageStatus = async (item) => {
    try {
      await axios.put(`${API}/admin/master-data/languages/${item._id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      fetchLanguages();
    } catch (error) { toast.error('Failed to update status'); }
  };

  // ==================== CONNECT CATEGORIES ====================
  const fetchConnectCategories = async () => {
    setConnectCategoriesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/master-data/connect-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectCategories(response.data.connect_categories || []);
    } catch (error) {
      toast.error('Failed to fetch connect categories');
    } finally { setConnectCategoriesLoading(false); }
  };

  const handleConnectCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editMode) {
        await axios.put(`${API}/admin/master-data/connect-categories/${currentItem._id}`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category updated');
      } else {
        await axios.post(`${API}/admin/master-data/connect-categories`, formData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Category created');
      }
      setDialogOpen(false);
      fetchConnectCategories();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save'); }
  };

  const deleteConnectCategory = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/master-data/connect-categories/${item._id}`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      toast.success('Deleted');
      fetchConnectCategories();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  const toggleConnectCategoryStatus = async (item) => {
    try {
      await axios.put(`${API}/admin/master-data/connect-categories/${item._id}`,
        { is_active: !item.is_active },
        { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }
      );
      fetchConnectCategories();
    } catch (error) { toast.error('Failed to update status'); }
  };


  // ==================== DIALOG HANDLERS ====================
  const openCreateDialog = async () => {
    // Ensure service types are loaded for categories
    if (activeSection === 'categories' && serviceTypes.length === 0) {
      await fetchServiceTypes();
    }
    
    if (activeSection === 'service-types' || activeSection === 'property-types' || activeSection === 'specializations' || activeSection === 'languages' || activeSection === 'connect-categories') {
      setFormData({ name: '', description: '', is_active: true });
    } else if (activeSection === 'categories') {
      setFormData({ name: '', description: '', status: 'active', service_type_ids: [] });
    }
    setEditMode(false);
    setCurrentItem(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (item) => {
    // Ensure service types are loaded for categories
    if (activeSection === 'categories' && serviceTypes.length === 0) {
      await fetchServiceTypes();
    }
    
    if (activeSection === 'service-types' || activeSection === 'property-types') {
      setFormData({
        name: item.name,
        description: item.description || '',
        is_active: item.is_active
      });
    } else if (activeSection === 'categories') {
      setFormData({
        name: item.name,
        description: item.description || '',
        status: item.status,
        service_type_ids: item.service_type_ids || []
      });
    } else if (activeSection === 'specializations' || activeSection === 'languages') {
      setFormData({
        name: item.name,
        description: item.description || '',
        is_active: item.is_active
      });
    } else if (activeSection === 'connect-categories') {
      setFormData({
        name: item.name,
        description: item.description || '',
        is_active: item.is_active
      });
    }
    setEditMode(true);
    setCurrentItem(item);
    setDialogOpen(true);
  };

  const handleSubmit = (e) => {
    if (activeSection === 'service-types') {
      handleServiceTypeSubmit(e);
    } else if (activeSection === 'categories') {
      handleCategorySubmit(e);
    } else if (activeSection === 'property-types') {
      handlePropertyTypeSubmit(e);
    } else if (activeSection === 'specializations') {
      handleSpecializationSubmit(e);
    } else if (activeSection === 'languages') {
      handleLanguageSubmit(e);
    } else if (activeSection === 'connect-categories') {
      handleConnectCategorySubmit(e);
    }
  };

  // ==================== RENDER SECTIONS ====================
  const renderServiceTypes = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">Service Types</h2>
          <p className="text-sm text-stone-600 mt-1">Manage provider service type classifications</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#1A4D2E] hover:bg-[#143D24]">
          <Plus size={18} className="mr-2" />
          Add Service Type
        </Button>
      </div>

      {serviceTypesLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
        </div>
      ) : serviceTypes.length === 0 ? (
        <Card className="p-12 text-center">
          <Database size={48} className="mx-auto text-stone-400 mb-4" />
          <p className="text-stone-600">No service types found</p>
          <Button onClick={openCreateDialog} className="mt-4">
            <Plus size={18} className="mr-2" />
            Add First Service Type
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {serviceTypes.map((item) => (
            <Card key={item._id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-[#1A4D2E] text-white px-3 py-1 rounded-full text-sm font-bold">
                      ID: {item._id}
                    </span>
                    <h3 className="text-lg font-semibold text-stone-900">{item.name}</h3>
                    <button
                      onClick={() => toggleServiceTypeStatus(item)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {item.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {item.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  <p className="text-sm text-stone-600">{item.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => openEditDialog(item)} variant="outline" size="sm">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => deleteServiceType(item)} variant="outline" size="sm" className="text-red-600">
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderCategories = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">Service Categories</h2>
          <p className="text-sm text-stone-600 mt-1">Manage service provider categories with numeric IDs</p>
        </div>
        <div className="flex gap-2">
          {categories.length === 0 && (
            <Button onClick={initializeCategories} className="bg-blue-600 hover:bg-blue-700">
              <PlayCircle size={18} className="mr-2" />
              Initialize
            </Button>
          )}
          <Button onClick={openCreateDialog} className="bg-[#1A4D2E] hover:bg-[#143D24]">
            <Plus size={18} className="mr-2" />
            Add Category
          </Button>
        </div>
      </div>

      {categoriesLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
        </div>
      ) : categories.length === 0 ? (
        <Card className="p-12 text-center">
          <Tag size={48} className="mx-auto text-stone-400 mb-4" />
          <p className="text-stone-600 mb-2">No service categories found</p>
          <p className="text-sm text-stone-500 mb-6">Initialize default categories or create your own</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={initializeCategories} className="bg-blue-600 hover:bg-blue-700">
              <PlayCircle size={18} className="mr-2" />
              Initialize Default
            </Button>
            <Button onClick={openCreateDialog} className="bg-[#1A4D2E]">
              <Plus size={18} className="mr-2" />
              Create Custom
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4">
          {categories.map((item) => (
            <Card key={item._id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-[#1A4D2E] text-white px-3 py-1 rounded-full text-sm font-bold">
                      ID: {item._id}
                    </span>
                    <h3 className="text-lg font-semibold text-stone-900">{item.name}</h3>
                    <button
                      onClick={() => toggleCategoryStatus(item)}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        item.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {item.status === 'active' ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {item.status === 'active' ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  <p className="text-sm text-stone-600 mb-3">{item.description}</p>
                  
                  {/* Display assigned service types */}
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-stone-500 mb-2">Service Types ({item.service_types?.length || 0}):</p>
                    <div className="flex flex-wrap gap-2">
                      {item.service_types && item.service_types.length > 0 ? (
                        item.service_types.map((st) => (
                          <span
                            key={st._id}
                            className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs"
                          >
                            <Database size={12} />
                            {st.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-xs text-stone-400 italic">No service types assigned</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => openEditDialog(item)} variant="outline" size="sm">
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => deleteCategory(item)} variant="outline" size="sm" className="text-red-600">
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderPropertyTypes = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">Property Types</h2>
          <p className="text-sm text-stone-600 mt-1">Manage property type classifications for open houses</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#1A4D2E] hover:bg-[#143D24]" data-testid="add-property-type-btn">
          <Plus size={18} className="mr-2" />
          Add Property Type
        </Button>
      </div>

      {propertyTypesLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div>
        </div>
      ) : propertyTypes.length === 0 ? (
        <Card className="p-12 text-center">
          <Home size={48} className="mx-auto text-stone-400 mb-4" />
          <p className="text-stone-600">No property types found</p>
          <Button onClick={openCreateDialog} className="mt-4 bg-[#1A4D2E]" data-testid="add-first-property-type-btn">
            <Plus size={18} className="mr-2" />
            Add First Property Type
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {propertyTypes.map((item) => (
            <Card key={item._id} className="p-4 hover:shadow-md transition-shadow" data-testid={`property-type-${item._id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-[#1A4D2E] text-white px-3 py-1 rounded-full text-sm font-bold">
                      ID: {item._id}
                    </span>
                    <h3 className="text-lg font-semibold text-stone-900">{item.name}</h3>
                    <button
                      onClick={() => togglePropertyTypeStatus(item)}
                      data-testid={`toggle-property-type-${item._id}`}
                      className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {item.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {item.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  <p className="text-sm text-stone-600">{item.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => openEditDialog(item)} variant="outline" size="sm" data-testid={`edit-property-type-${item._id}`}>
                    <Edit size={16} className="mr-1" />
                    Edit
                  </Button>
                  <Button onClick={() => deletePropertyType(item)} variant="outline" size="sm" className="text-red-600" data-testid={`delete-property-type-${item._id}`}>
                    <Trash2 size={16} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderStates = () => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">US States</h2>
          <p className="text-sm text-stone-600 mt-1">All 50 United States (Read-only reference)</p>
        </div>
      </div>

      <Card className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {states.map((state) => (
            <div
              key={state.code}
              className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200 hover:bg-stone-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[#1A4D2E]" />
                <span className="font-semibold text-stone-900">{state.code}</span>
                <span className="text-sm text-stone-600">-</span>
                <span className="text-sm text-stone-700">{state.name}</span>
              </div>
              <CheckCircle size={16} className="text-green-600" />
            </div>
          ))}
        </div>
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> US States are system-defined and used across the application in dropdowns. 
            They cannot be modified to maintain data consistency.
          </p>
        </div>
      </Card>
    </div>
  );

  const renderMasterList = (title, subtitle, items, loading, onDelete, onToggle) => (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-stone-900">{title}</h2>
          <p className="text-sm text-stone-600 mt-1">{subtitle}</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-[#1A4D2E] hover:bg-[#143D24]">
          <Plus size={18} className="mr-2" />
          Add {title.replace(/s$/, '')}
        </Button>
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1A4D2E] mx-auto"></div></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Database size={48} className="mx-auto text-stone-400 mb-4" />
          <p className="text-stone-600">No {title.toLowerCase()} found</p>
          <Button onClick={openCreateDialog} className="mt-4"><Plus size={18} className="mr-2" />Add First</Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((item) => (
            <Card key={item._id} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-[#1A4D2E] text-white px-3 py-1 rounded-full text-sm font-bold">ID: {item._id}</span>
                    <h3 className="text-lg font-semibold text-stone-900">{item.name}</h3>
                    <button onClick={() => onToggle(item)} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {item.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                      {item.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                  {item.description && <p className="text-sm text-stone-600">{item.description}</p>}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button onClick={() => openEditDialog(item)} variant="outline" size="sm"><Edit size={16} className="mr-1" />Edit</Button>
                  <Button onClick={() => onDelete(item)} variant="outline" size="sm" className="text-red-600"><Trash2 size={16} className="mr-1" />Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderSpecializations = () => renderMasterList('Specializations', 'Manage agent/provider specialization areas', specializations, specializationsLoading, deleteSpecialization, toggleSpecializationStatus);
  const renderLanguages = () => renderMasterList('Languages Spoken', 'Manage available language options for professionals', languages, languagesLoading, deleteLanguage, toggleLanguageStatus);
  const renderConnectCategories = () => renderMasterList('Connect Categories', 'Manage forum categories for Albriks Connect', connectCategories, connectCategoriesLoading, deleteConnectCategory, toggleConnectCategoryStatus);

  return (
    <div className="min-h-screen bg-stone-50 flex">
      {/* Left Sidebar */}
      <div className="w-64 bg-white border-r border-stone-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-stone-200">
          <Button
            onClick={() => navigate('/admin/dashboard')}
            variant="ghost"
            size="sm"
            className="w-full justify-start text-stone-600 hover:text-[#1A4D2E] mb-3"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Dashboard
          </Button>
          <h2 className="text-lg font-bold text-[#1A4D2E]">Master Data</h2>
          <p className="text-xs text-stone-600 mt-1">System Configuration</p>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2">
          <button
            onClick={() => setActiveSection('service-types')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'service-types'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Database size={20} />
            <span className="font-medium">Service Types</span>
          </button>

          <button
            onClick={() => setActiveSection('categories')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'categories'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Tag size={20} />
            <span className="font-medium">Service Categories</span>
          </button>

          <button
            onClick={() => setActiveSection('property-types')}
            data-testid="nav-property-types"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'property-types'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Home size={20} />
            <span className="font-medium">Property Types</span>
          </button>

          <button
            onClick={() => setActiveSection('states')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'states'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <MapPin size={20} />
            <span className="font-medium">US States</span>
          </button>

          <button
            onClick={() => setActiveSection('specializations')}
            data-testid="nav-specializations"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'specializations'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Award size={20} />
            <span className="font-medium">Specializations</span>
          </button>

          <button
            onClick={() => setActiveSection('languages')}
            data-testid="nav-languages"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'languages'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Globe size={20} />
            <span className="font-medium">Languages Spoken</span>
          </button>

          <button
            onClick={() => setActiveSection('connect-categories')}
            data-testid="nav-connect-categories"
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeSection === 'connect-categories'
                ? 'bg-[#1A4D2E] text-white'
                : 'text-stone-700 hover:bg-stone-100'
            }`}
          >
            <Tag size={20} />
            <span className="font-medium">Connect Categories</span>
          </button>
        </nav>

        {/* Footer Stats */}
        <div className="p-4 border-t border-stone-200 space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Service Types:</span>
            <span className="font-semibold text-stone-900">{serviceTypes.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Categories:</span>
            <span className="font-semibold text-stone-900">{categories.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Property Types:</span>
            <span className="font-semibold text-stone-900">{propertyTypes.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">States:</span>
            <span className="font-semibold text-stone-900">50</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Specializations:</span>
            <span className="font-semibold text-stone-900">{specializations.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Languages:</span>
            <span className="font-semibold text-stone-900">{languages.length}</span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-stone-600">Connect Categories:</span>
            <span className="font-semibold text-stone-900">{connectCategories.length}</span>
          </div>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="p-6 max-w-6xl mx-auto">
          {activeSection === 'service-types' && renderServiceTypes()}
          {activeSection === 'categories' && renderCategories()}
          {activeSection === 'property-types' && renderPropertyTypes()}
          {activeSection === 'states' && renderStates()}
          {activeSection === 'specializations' && renderSpecializations()}
          {activeSection === 'languages' && renderLanguages()}
          {activeSection === 'connect-categories' && renderConnectCategories()}
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1A4D2E]">
              {editMode ? 'Edit' : 'Create'} {activeSection === 'service-types' ? 'Service Type' : activeSection === 'property-types' ? 'Property Type' : activeSection === 'specializations' ? 'Specialization' : activeSection === 'languages' ? 'Language' : 'Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={activeSection === 'property-types' ? 'e.g., Single Family, Condo, Townhouse' : 'e.g., Plumber, Finance'}
                required
              />
            </div>

            <div>
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this service..."
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                value={activeSection === 'service-types' || activeSection === 'property-types' || activeSection === 'specializations' || activeSection === 'languages' ? (formData.is_active ? 'active' : 'inactive') : formData.status}
                onChange={(e) => {
                  if (activeSection === 'service-types' || activeSection === 'property-types' || activeSection === 'specializations' || activeSection === 'languages') {
                    setFormData({ ...formData, is_active: e.target.value === 'active' });
                  } else {
                    setFormData({ ...formData, status: e.target.value });
                  }
                }}
                className="w-full px-3 py-2 border border-stone-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Service Types Multi-Select for Categories */}
            {activeSection === 'categories' && (
              <div>
                <Label htmlFor="service_types">Service Types (Optional)</Label>
                <div className="border border-stone-300 rounded-md p-3 max-h-48 overflow-y-auto">
                  {serviceTypes.length === 0 ? (
                    <p className="text-sm text-stone-500">No service types available</p>
                  ) : (
                    <div className="space-y-2">
                      {serviceTypes.map((st) => (
                        <label key={st._id} className="flex items-center gap-2 cursor-pointer hover:bg-stone-50 p-2 rounded">
                          <input
                            type="checkbox"
                            checked={(formData.service_type_ids || []).includes(st._id)}
                            onChange={(e) => {
                              const currentIds = formData.service_type_ids || [];
                              if (e.target.checked) {
                                setFormData({ ...formData, service_type_ids: [...currentIds, st._id] });
                              } else {
                                setFormData({ ...formData, service_type_ids: currentIds.filter(id => id !== st._id) });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm">
                            <span className="font-semibold">ID {st._id}:</span> {st.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                <p className="text-xs text-stone-500 mt-1">
                  Selected: {(formData.service_type_ids || []).length} service type(s)
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#1A4D2E] hover:bg-[#143D24]">
                <Save size={16} className="mr-2" />
                {editMode ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MasterDataManagement;
